import { and, count, eq, gt, inArray, isNotNull, sql } from "drizzle-orm";

import type { WsCommandAlias } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("query");

import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { account, feed, permission, workspaceCommandAlias, workspaceMembership } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

export async function bulkUpsertWorkspaceCommandAlias(records: WsCommandAlias[]) {
  if (records.length === 0) return;

  const data = records.map((c) => ({
    id: c.id,
    alias: c.alias,
    workspaceId: c.workspaceId,
    workspaceMembershipId: c.workspaceMembershipId,
    feedId: c.feedId,
    createdAt: c.createdAt,
  }));

  const deduped = deduplicateByKeyValue(data, (item) => item.id);

  await bulkInsert(workspaceCommandAlias, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [workspaceCommandAlias.id],
      set: {
        alias: sql`"excluded"."alias"`,
        workspaceId: sql`"excluded"."workspaceId"`,
        workspaceMembershipId: sql`"excluded"."workspaceMembershipId"`,
        feedId: sql`"excluded"."feedId"`,
      },
    },
  });

  const feedIds = Array.from(new Set(records.map((r) => r.feedId).filter((id): id is string => Boolean(id))));
  if (feedIds.length > 0) {
    try {
      await db.update(feed).set({ isAliasFeed: true }).where(inArray(feed.id, feedIds)).execute();
    } catch (error) {
      log.error("[BULK UPSERT WORKSPACE COMMAND ALIAS] Error updating feeds", error);
    }
  }

  emitTableUpdates(["workspace_command_alias", "feed"]);
}

export const multipleWorkspaceAliasInsideFeed = (workspaceId: string) => {
  const query = db
    .select({
      feedId: workspaceCommandAlias.feedId,
      aliasFeedCount: sql`count(${workspaceCommandAlias.feedId}) as aliasFeedCount`,
    })
    .from(workspaceCommandAlias)
    .groupBy(workspaceCommandAlias.feedId)
    .where(and(eq(workspaceCommandAlias.workspaceId, workspaceId), isNotNull(workspaceCommandAlias.feedId)))
    .having(gt(count(workspaceCommandAlias.feedId), 1));
  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch channel list", query);
  return query;
};
export const hasAliasChannelsInWorkspace = (workspaceId: string) => {
  const query = db
    .select({
      feedId: workspaceCommandAlias.feedId,
    })
    .from(workspaceCommandAlias)
    .groupBy(workspaceCommandAlias.feedId)
    .where(and(eq(workspaceCommandAlias.workspaceId, workspaceId), isNotNull(workspaceCommandAlias.feedId)))
    .limit(1);
  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch channel list", query);
  return query;
};
export const fetchFeedCommandAliasesQuery = (feedId: string) => {
  const query = db
    .select({
      id: account.id,
      name: account.name,
      alias: workspaceCommandAlias.alias,
      title: feed.title,
    })
    .from(workspaceCommandAlias)
    .innerJoin(workspaceMembership, eq(workspaceMembership.id, workspaceCommandAlias.workspaceMembershipId))
    .innerJoin(account, eq(account.id, workspaceMembership.accountId))
    .innerJoin(feed, eq(feed.id, feedId))
    .where(
      and(
        eq(workspaceCommandAlias.feedId, feedId),
        eq(workspaceMembership.id, workspaceCommandAlias.workspaceMembershipId),
        isNotNull(workspaceCommandAlias.feedId),
      ),
    );

  if (!feedId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Channels with Permissions", query);
  return query;
};

export const fetchFeedsForAccountQuery = (accountId: string, workspaceId: string) => {
  const query = db
    .select({
      id: feed.id,
      title: feed.title,
      feedType: feed.feedType,
      isAliasFeed: feed.isAliasFeed,
      latestActivity: feed.latestActivity,
      workspaceId: feed.workspaceId,
      alias: workspaceCommandAlias.alias,
    })
    .from(workspaceCommandAlias)
    .innerJoin(workspaceMembership, eq(workspaceMembership.id, workspaceCommandAlias.workspaceMembershipId))
    .innerJoin(feed, eq(feed.id, workspaceCommandAlias.feedId))
    .where(
      and(
        eq(workspaceMembership.accountId, accountId),
        eq(workspaceCommandAlias.workspaceId, workspaceId),
        isNotNull(workspaceCommandAlias.feedId),
      ),
    );

  if (!accountId || !workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch feeds for account", query);
  return query;
};

export const fetchAccessibleAliasKeysQuery = ({
  workspaceId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
}) => {
  const query = db
    .selectDistinct({ alias: workspaceCommandAlias.alias })
    .from(workspaceCommandAlias)
    .innerJoin(feed, eq(feed.id, workspaceCommandAlias.feedId))
    .innerJoin(workspaceMembership, eq(workspaceMembership.id, feed.aliasMembershipId))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.enabled, true),
        eq(permission.name, "write"),
      ),
    )
    .where(
      and(
        eq(workspaceCommandAlias.workspaceId, workspaceId),
        isNotNull(workspaceCommandAlias.feedId),
        eq(workspaceMembership.status, "active"),
      ),
    );
  if (!workspaceId || !workspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch accessible alias keys", query);
  return query;
};

export const fetchWorkspaceCommandAliases = ({
  workspaceId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
}) => {
  const query = db
    .select({
      id: workspaceCommandAlias.id,
      alias: workspaceCommandAlias.alias,
      feedId: workspaceCommandAlias.feedId,
      createdAt: workspaceCommandAlias.createdAt,
      feedTitle: feed.title,
    })
    .from(workspaceCommandAlias)
    .innerJoin(feed, eq(feed.id, workspaceCommandAlias.feedId))
    .innerJoin(
      permission,
      and(
        eq(feed.id, permission.feedId),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
        eq(permission.enabled, true),
        eq(permission.name, "write"),
      ),
    )
    .where(eq(workspaceCommandAlias.workspaceId, workspaceId));
  if (!workspaceId || !workspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Fetch workspace command aliases", query);
  return query;
};
